/**
 * Browser WYSIWYG export: render preview canvas frames + FFmpeg.wasm + optional narration MP3.
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { drawLearningPreviewFrame } from './previewRenderer';
import { mergeSegmentsIntoRendered } from './mergeRendered';
import { preloadLearningImages } from './imagePreload';
import { resolveLogoPreviewUrl } from './logoBrand';

const FFMPEG_CORE_BASE = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd';

let ffmpegSingleton = null;
let ffmpegLoadPromise = null;

async function getFfmpeg(onProgress) {
  if (ffmpegSingleton?.loaded) return ffmpegSingleton;
  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      onProgress?.('Loading video compiler…');
      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress }) => {
        if (typeof progress === 'number') {
          onProgress?.(`Encoding… ${Math.round(progress * 100)}%`);
        }
      });
      await ffmpeg.load({
        coreURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      ffmpeg.loaded = true;
      ffmpegSingleton = ffmpeg;
      return ffmpeg;
    })();
  }
  return ffmpegLoadPromise;
}

function resolveMediaUrl(url, backendUrl) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${backendUrl}${url}`;
  return `${backendUrl}/${url}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function loadLogoIntoCache(editorState, backendUrl, imageCache) {
  const url = editorState?.logo?.url;
  if (!url) return;
  const src = resolveLogoPreviewUrl(url, backendUrl);
  if (imageCache.__logo__ && imageCache[src]) return;
  await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageCache.__logo__ = img;
      imageCache[url] = img;
      imageCache[src] = img;
      resolve();
    };
    img.onerror = () => resolve();
    img.src = src;
  });
}

async function renderTimelineMp4({
  rendered,
  segments,
  editorState,
  backendUrl,
  onProgress,
}) {
  const merged = mergeSegmentsIntoRendered(rendered, segments);
  if (!merged?.elements?.length) throw new Error('Nothing to export — generate a timeline first');

  const w = Number(merged.width) || 1280;
  const h = Number(merged.height) || 720;
  const fps = Number(merged.fps) || 30;
  const total = Number(merged.total_duration) || 10;
  const frameCount = Math.max(1, Math.ceil(total * fps));

  onProgress?.('Preloading images…');
  const imageCache = await preloadLearningImages(merged, backendUrl);
  await loadLogoIntoCache(editorState, backendUrl, imageCache);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');

  const ffmpeg = await getFfmpeg(onProgress);
  const pad = String(frameCount).length;

  for (let i = 0; i < frameCount; i += 1) {
    const t = i / fps;
    drawLearningPreviewFrame(ctx, merged, t, editorState || {}, {
      omitEditableLayers: false,
      imageCache,
    });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Frame capture failed');
    const name = `frame_${String(i).padStart(pad, '0')}.png`;
    await ffmpeg.writeFile(name, await fetchFile(blob));
    if (i % Math.max(1, Math.floor(fps)) === 0) {
      onProgress?.(`Rendering frames ${i + 1}/${frameCount}`);
    }
  }

  onProgress?.('Encoding video…');
  await ffmpeg.exec([
    '-framerate', String(fps),
    '-i', `frame_%0${pad}d.png`,
    '-c:v', 'libx264',
    '-pix_fmt', 'yuv420p',
    '-preset', 'veryfast',
    'video_only.mp4',
  ]);

  return { ffmpeg, frameCount, fps, total };
}

async function muxNarration(ffmpeg, narrationAudioUrl, backendUrl, onProgress) {
  if (!narrationAudioUrl) return 'video_only.mp4';
  onProgress?.('Mixing narration audio…');
  const audioSrc = resolveMediaUrl(narrationAudioUrl, backendUrl);
  const res = await fetch(audioSrc);
  if (!res.ok) throw new Error('Could not fetch narration audio');
  const buf = await res.arrayBuffer();
  await ffmpeg.writeFile('narration.mp3', new Uint8Array(buf));
  await ffmpeg.exec([
    '-i', 'video_only.mp4',
    '-i', 'narration.mp3',
    '-c:v', 'copy',
    '-c:a', 'aac',
    '-shortest',
    'output.mp4',
  ]);
  return 'output.mp4';
}

/**
 * @param {object} opts
 */
export async function exportLearningTutorialFromPreview(opts) {
  const {
    rendered,
    segments,
    editorState,
    narrationAudioUrl,
    backendUrl = '',
    filename = 'lesson',
    onProgress,
    download = true,
  } = opts;

  const { ffmpeg } = await renderTimelineMp4({
    rendered,
    segments,
    editorState,
    backendUrl,
    onProgress,
  });

  const finalName = await muxNarration(ffmpeg, narrationAudioUrl, backendUrl, onProgress);
  onProgress?.('Saving file…');
  const data = await ffmpeg.readFile(finalName);
  const blob = new Blob([data.buffer], { type: 'video/mp4' });
  if (download) downloadBlob(blob, `${filename}.mp4`);
  return blob;
}

export async function exportAndUploadLearningTutorial(opts) {
  const { projectId, api, filename, ...rest } = opts;
  const blob = await exportLearningTutorialFromPreview({ ...rest, filename, download: false });
  const fd = new FormData();
  fd.append('file', blob, `${filename || 'lesson'}.mp4`);
  await api.post(`/learning-tutorials/projects/${projectId}/upload-final`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return blob;
}
